import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const useElementSize = <T extends HTMLElement>() => {
  const [node, setNode] = useState<T | null>(null);
  const ref = useCallback((el: T | null) => setNode(el), []);

  const [size, setSize] = useState({ width: 1, height: 1 }); // start with non-zero
  const rafRef = useRef<number | null>(null);

  const measure = () => {
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);

    setSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
  };

  useLayoutEffect(() => {
    if (!node) return;

    // 1) Measure immediately (before paint)
    measure();

    // 2) Measure again on next frame for layout stability
    rafRef.current = requestAnimationFrame(measure);

    // 3) Watch for future size changes
    const ro = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    });
    ro.observe(node);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [node]);

  return { ref, width: size.width, height: size.height };
};
